import { LoginWithOtpResponse } from '@/use-cases/login-with-otp';
import React, { useContext, useEffect, useState } from 'react';
import { UserProfile } from '@/entities/user-profile';
import { getUserInfo } from '@/use-cases/get-user-info';

export type UserInfo = {
	sessionId: string;
	userId: string;
	userName: string;
	email: string;
};

type AuthContextValue = {
	isAuthenticated: boolean;
	kycUrl: string | null;
	userProfile: UserProfile | null;
	isFetchingUserProfile: boolean;
	saveToken: (token: LoginWithOtpResponse) => void;
	setKycUrl: (url: string | null) => void;
};

export const AuthContext = React.createContext<AuthContextValue>({
	isAuthenticated: false,
	kycUrl: null,
	userProfile: null,
	isFetchingUserProfile: false,
	saveToken: () => {
		return;
	},
	setKycUrl: () => {
		return;
	},
});

export const useAuthContext = () => {
	const auth = useContext(AuthContext);
	if (auth === null) {
		throw new Error(
			'useAuthContext can only be used within a AuthContextProvider',
		);
	}
	return auth;
};

const AuthContextProvider: React.FC = (props) => {
	const isTokenValid = () => {
		const sessionTkn: LoginWithOtpResponse = JSON.parse(
			localStorage.getItem('user-info') || '{}',
		);
		const b = localStorage.getItem('token-expir-date') || '0';
		return sessionTkn.id_token && Date.now() / 1000 < parseInt(b)
			? true
			: false;
	};

	const [isUserAuthenticated, setIsUserAuthenticated] =
		useState(isTokenValid());
	const [kycUrl, setKycUrl] = useState<string | null>(null);
	const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
	const [isFetchingUserProfile, setIsFetchingUserProfile] = useState(false);

	useEffect(() => {
		if (!isUserAuthenticated) return;
		setIsFetchingUserProfile(true);
		getUserInfo()
			.then(setUserProfile)
			.catch(console.debug)
			.finally(() => setIsFetchingUserProfile(false));
	}, [isUserAuthenticated]);

	const saveToken = (token: LoginWithOtpResponse) => {
		localStorage.setItem('user-info', JSON.stringify(token));

		const expDate = Math.round(Date.now() / 1000) + token.expires_in;

		localStorage.setItem('token-expir-date', expDate.toString());
		setIsUserAuthenticated(true);
	};

	const contextValue: AuthContextValue = {
		isAuthenticated: isUserAuthenticated,
		kycUrl,
		userProfile,
		isFetchingUserProfile,
		setKycUrl,
		saveToken,
	};

	return (
		<AuthContext.Provider value={contextValue}>
			{props.children}
		</AuthContext.Provider>
	);
};

export default AuthContextProvider;
